import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Item from '../../components/Item';
import clearObjProps from '../../helpers/clearObjProps';
import { useHistory, useParams } from 'react-router-dom';
import translations from '../../translations/de';
import Paths from '../../constants/paths';
import getFormInput from './getFormInput';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import useModal from '../../customHooks/useModal';
import { FaSyncAlt, FaPaperPlane, FaListAlt, FaTrash } from 'react-icons/fa';

const Webhook = ({ store }) => {
    const { webhook_id } = useParams();
    const history = useHistory();
    const [edit, setEdit] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [rotatedSecret, setRotatedSecret] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [actionError, setActionError] = useState(null);

    const deleteModalRef = useRef(null);
    const [deleteModal, setDeleteModal] = useModal(false, deleteModalRef);
    const secretModalRef = useRef(null);

    const subscriptionModel = clearObjProps(
        { ...store.selectedSubscription },
        ['id', 'created_at', 'updated_at'],
    );

    useEffect(() => {
        store.getSubscription(webhook_id);
        return () => {
            store.selectSubscription(undefined);
        };
    }, [store, webhook_id]);

    const onSubmitHandler = (e) => {
        error && setError(null);
        setLoading(true);
        store
            .updateSubscription({ ...e })
            .then(() => {
                setEdit(false);
                setLoading(false);
            })
            .catch((err) => {
                setError(err);
                setLoading(false);
            });
    };

    const handleRotate = async () => {
        setActionError(null);
        try {
            const secret = await store.rotateSecret(store.selectedSubscription.id);
            setRotatedSecret(secret);
        } catch (err) {
            setActionError(err);
        }
    };

    const handleSendTest = async () => {
        setActionError(null);
        setTestResult(null);
        try {
            const res = await store.sendTest(store.selectedSubscription.id);
            setTestResult(res);
        } catch (err) {
            setActionError(err);
        }
    };

    const handleDelete = async () => {
        await store.deleteSubscription(store.selectedSubscription.id);
        setDeleteModal(false);
        history.push(Paths.WEBHOOKS);
    };

    return store.selectedSubscription ? (
        <>
            <Item
                links={[{ path: Paths.WEBHOOKS, name: translations.webhooks }]}
                name={store?.selectedSubscription?.name}
                id={`[ webhook_ID: ${store.selectedSubscription.id} ]`}
                itemModel={subscriptionModel}
                edit={edit}
                onSubmit={onSubmitHandler}
                onEdit={() => setEdit(true)}
                onCancel={() => {
                    error && setError(null);
                    setEdit(false);
                }}
                error={error}
                loading={loading}
                determineInputType={({ control, name, register, error, disabled, index }) =>
                    getFormInput({ name, register, control, error, disabled, index })
                }
            >
                <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button type="button" label={translations.rotate_secret} onClick={handleRotate}>
                        <FaSyncAlt style={{ marginRight: 6 }} />
                    </Button>
                    <Button type="button" label={translations.send_test} onClick={handleSendTest}>
                        <FaPaperPlane style={{ marginRight: 6 }} />
                    </Button>
                    <Button
                        type="button"
                        label={translations.deliveries}
                        onClick={() =>
                            history.push(`/webhooks/${store.selectedSubscription.id}/deliveries`)
                        }
                    >
                        <FaListAlt style={{ marginRight: 6 }} />
                    </Button>
                    <Button
                        type="button"
                        label={translations.delete}
                        theme="dark"
                        onClick={() => setDeleteModal(true)}
                    >
                        <FaTrash style={{ marginRight: 6 }} />
                    </Button>
                </div>
                {testResult && (
                    <div
                        style={{
                            marginBottom: 12,
                            padding: 10,
                            background: '#eef9ee',
                            border: '1px solid #c8e6c9',
                        }}
                    >
                        {translations.test_enqueued_for_game} #{testResult.game_id}
                    </div>
                )}
                {actionError && (
                    <div
                        style={{
                            marginBottom: 12,
                            padding: 10,
                            background: '#fdecea',
                            border: '1px solid #f5c6cb',
                            color: '#611a15',
                        }}
                    >
                        {actionError.message || JSON.stringify(actionError)}
                    </div>
                )}
            </Item>

            {rotatedSecret && (
                <Modal
                    title={translations.webhook_secret_title}
                    ref={secretModalRef}
                    onClose={() => setRotatedSecret(null)}
                    maxWidth="600px"
                >
                    <p style={{ color: '#333', marginBottom: '10px' }}>
                        {translations.webhook_secret_warning}
                    </p>
                    <pre
                        style={{
                            background: '#f5f5f5',
                            color: '#000',
                            padding: '10px',
                            border: '1px solid #ddd',
                            wordBreak: 'break-all',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {rotatedSecret}
                    </pre>
                    <div className="Modal-Controls" style={{ marginTop: 16 }}>
                        <Button
                            label={translations.copy}
                            onClick={() =>
                                navigator.clipboard &&
                                navigator.clipboard.writeText(rotatedSecret)
                            }
                        />
                        <Button
                            label={translations.close}
                            onClick={() => setRotatedSecret(null)}
                            style={{ marginLeft: '20px' }}
                        />
                    </div>
                </Modal>
            )}

            {deleteModal && (
                <Modal
                    title={translations.delete_webhook}
                    ref={deleteModalRef}
                    onClose={() => setDeleteModal(false)}
                    maxWidth="400px"
                >
                    <p style={{ color: '#333', marginBottom: '20px' }}>
                        {`Are you sure you want to delete subscription #${store.selectedSubscription.id}?`}
                    </p>
                    <div className="Modal-Controls">
                        <Button label="Yes" onClick={handleDelete} style={{ width: '80px' }} />
                        <Button
                            label="No"
                            onClick={() => setDeleteModal(false)}
                            style={{ width: '80px', marginLeft: '20px' }}
                        />
                    </div>
                </Modal>
            )}
        </>
    ) : null;
};

export default observer(Webhook);
